/**
 * Modulo Solicitudes: puerta de entrada por departamento.
 *
 * El usuario elige a que departamento le pide y entra al flujo de ese
 * departamento. Hoy hay uno (Recursos Humanos, migrado de Flokzu) y la lista
 * sale de config/departamentosSolicitudes.ts, asi que sumar otro es agregar una
 * entrada ahi mas sus rutas — no tocar esta pantalla.
 *
 * Se filtra por los permisos de modulo de cada departamento: alguien que solo
 * puede pedir a RRHH ve solo RRHH. EMSYS revalida igual del otro lado.
 */
import {
  IonCard,
  IonCardContent,
  IonContent,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonNote,
  IonPage,
  IonText,
} from "@ionic/react";
import { chevronForwardOutline } from "ionicons/icons";
import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

import Header from "src/components/Header";
import { cargarPermisoCapacitacion } from "src/services/solicitudCapacitacion";
import { cargarPermisoRrhh } from "src/services/solicitudesRRHH";
import { cargarPermisoSst } from "src/services/sst";
import {
  DEPARTAMENTOS_SOLICITUDES,
  ICONO_SOLICITUDES,
  departamentoVisible,
} from "src/config/departamentosSolicitudes";
import { loadModulePermissions } from "src/config/modulePermissions";

interface Props {
  cargo?: string | null;
}

const Solicitudes: React.FC<Props> = ({ cargo }) => {
  const history = useHistory();

  // RRHH, SST y Gestion de Talento dependen del backend (administrativos por
  // tipo de funcionario). Si se entra directo a esta pantalla puede no estar cargado:
  // se pide y se recalcula al llegar.
  const [permisosEmsys, setPermisosEmsys] = useState(0);
  useEffect(() => {
    let vivo = true;
    Promise.all([cargarPermisoRrhh(), cargarPermisoSst(), cargarPermisoCapacitacion()]).then(() => {
      if (vivo) setPermisosEmsys((n) => n + 1);
    });
    return () => {
      vivo = false;
    };
  }, []);

  const disponibles = useMemo(() => {
    const permisos = loadModulePermissions();
    return DEPARTAMENTOS_SOLICITUDES.filter((d) =>
      departamentoVisible(d, cargo ?? undefined, permisos),
    );
    // permisosEmsys: recalcular cuando llegan los permisos de EMSYS.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cargo, permisosEmsys]);

  return (
    <IonPage>
      <Header titulo="Solicitudes" />
      <IonContent id="solicitudes">
        {disponibles.length === 0 ? (
          <IonCard>
            <IonCardContent className="ion-text-center">
              <IonIcon
                icon={ICONO_SOLICITUDES}
                style={{ fontSize: 48, color: "var(--ion-color-medium)" }}
              />
              <IonText color="medium">
                <p>No tenés departamentos habilitados para hacer solicitudes.</p>
              </IonText>
            </IonCardContent>
          </IonCard>
        ) : (
          <>
            <IonNote
              color="medium"
              className="ion-padding-start ion-padding-top"
              style={{ display: "block", fontSize: "0.9rem" }}
            >
              ¿A qué departamento querés pedir?
            </IonNote>

            <IonList lines="full">
              {disponibles.map((departamento) => (
                <IonItem
                  key={departamento.id}
                  button
                  detail={false}
                  onClick={() => history.push(departamento.ruta)}
                >
                  <IonIcon
                    icon={departamento.icono}
                    slot="start"
                    color="primary"
                  />
                  <IonLabel className="ion-text-wrap">
                    <h2 style={{ fontWeight: 600 }}>{departamento.titulo}</h2>
                    <p style={{ fontSize: "0.8rem" }}>
                      {departamento.descripcion}
                    </p>
                  </IonLabel>
                  <IonIcon
                    icon={chevronForwardOutline}
                    slot="end"
                    color="medium"
                  />
                </IonItem>
              ))}
            </IonList>
          </>
        )}
      </IonContent>
    </IonPage>
  );
};

export default Solicitudes;
